import { Box, Grid } from "@mui/material";
import { useTokens } from "api/accessTokens";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { hasViewAccessToResource, useSettings } from "api/general";
import { useRoles } from "api/roles";
import { FormButtons } from "components/FormControls/FormButtons";
import { FormSection } from "components/FormControls/FormSection";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { TextInput } from "components/FormControls/TextInput";
import { Toggle } from "components/FormControls/Toggle";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik } from "formik";
import { useMessage } from "hooks/intl";
import { useManageInfiniteScroll } from "hooks/useManageInfiniteScroll";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminBackendModel, AdminFormFields } from "types/general";
import { backendErrors } from "utilities/backendErrors";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import * as Yup from "yup";
import { AccessToken } from "./AccessToken";

export function AdminForm(props: {
  editMode?: boolean;
  initialValues?: AdminBackendModel;
}) {
  const { notify } = useNotifications();
  const navigate = useNavigate();
  const { message } = useMessage();
  const { addAdmin, editAdmin } = useSettings();
  const { useInfiniteRolesList } = useRoles();
  const { useTokensList } = useTokens();
  const { id: adminId } = useParams<{ id: string }>();
  const { data } = useTokensList(adminId);

  const [open, setOpen] = useState(false);
  const [searchedPhrase, setSearchedPhrase] = useState("");

  const tokensList = data?.items;

  const {
    data: infiniteRolesList,
    size: rolesSize,
    setSize: setRolesSize,
    isValidating,
  } = useInfiniteRolesList({
    filters: searchedPhrase
      ? [
          {
            condition: "like",
            query: "name",
            value: searchedPhrase,
          },
        ]
      : [],
    order: "asc",
    sortKey: "name",
  });

  const { infiniteData: rolesList, anyElementsToFetch: anyRolesToFetch } =
    useManageInfiniteScroll(infiniteRolesList || []);

  const formattedRoleList = rolesList.map(
    (role: { id: string; name: string }) => ({
      id: role?.id.toString(),
      label: role?.name,
    }),
  );

  const hasViewAdminPermission = hasViewAccessToResource("ADMIN");

  const initialValues =
    props.editMode && props.initialValues
      ? {
          email: props.initialValues?.email,
          firstName: props.initialValues?.firstName,
          lastName: props.initialValues?.lastName,
          phone: props.initialValues?.phone,
          notificationsEnabled:
            !!props.initialValues.settings?.notificationsEnabled,
          roles: props.initialValues.roles?.[0]?.id,
          roleLabel: props.initialValues.roles?.[0]?.name,
          isActive: props.initialValues?.isActive,
        }
      : {
          apiKey: "",
          email: "",
          firstName: "",
          isActive: false,
          lastName: "",
          notificationsEnabled: false,
          phone: "",
          plainPassword: "",
          plainPassword2: "",
          roleLabel: "",
          roles: "",
        };

  const isFormDataReady =
    (props.editMode && props.initialValues) || !props.editMode;
  const adminRoleID = props.initialValues?.roles?.[0]?.id || "";
  const adminRoleName = props.initialValues?.roles?.[0]?.name || "";

  const getStringValue = (value?: string) => (!!value ? value : undefined);

  return (
    <Formik
      enableReinitialize
      initialStatus={{}}
      initialValues={initialValues}
      validationSchema={Yup.object().shape({
        external: Yup.boolean(),
        plainPassword: Yup.string().when("external", {
          is: (external: boolean) => !external,
          then: () =>
            Yup.string()
              .nullable()
              .test(
                "passwords-match",
                message({ id: "passwordsNotEqualError" }),
                function (value) {
                  const { plainPassword2 } = this.parent;
                  return plainPassword2 === value;
                },
              ),
        }),
        plainPassword2: Yup.string().when("external", {
          is: (external: boolean) => !external,
          then: () =>
            Yup.string()
              .nullable()
              .test(
                "passwords-match",
                message({ id: "passwordsNotEqualError" }),
                function (value) {
                  const { plainPassword } = this.parent;
                  return plainPassword === value;
                },
              ),
        }),
      })}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        setStatus({});

        const properties: AdminFormFields = {
          email: values.email,
          firstName: getStringValue(values.firstName),
          lastName: getStringValue(values.lastName),
          phone: getStringValue(values.phone),
          roles: [Number(values.roles)],
          isActive: values.isActive ? "1" : "0",
          notificationsEnabled: values.notificationsEnabled,
          plainPassword: getStringValue(values.plainPassword),
        };

        try {
          const response =
            props.editMode && props.initialValues
              ? await editAdmin(props.initialValues.id, properties)
              : await addAdmin(properties);

          if (response) {
            navigate(links.settings);
            notify(
              message({ id: props.editMode ? "settingsSaved" : "adminAdded" }),
            );
          }
        } catch (exception) {
          setSubmitting(false);
          notify(getErrorMessage(exception));
          const errors = backendErrors(getBackendErrors(exception));
          setStatus({ ...errors, plainPassword2: errors.plainPassword });
          focusError();
        }
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      {({
        handleSubmit,
        isSubmitting,
        status,
        dirty,
        values,
        initialValues,
      }) => {
        const formError = status?.[""] || status?.admin;

        return (
          <Form onSubmit={handleSubmit}>
            <FormSection
              isLoading={!isFormDataReady}
              sectionError={formError}
              title={message({ id: "basicInformation" })}
            >
              <Grid container spacing={12}>
                <Grid xs={6} item>
                  <TextInput
                    data-hj-suppress
                    data-test-id="name-input"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "firstName" })}
                    name="firstName"
                    width="100%"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    data-hj-suppress
                    data-test-id="last-name-input"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "lastName" })}
                    name="lastName"
                    width="100%"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    data-hj-suppress
                    data-test-id="email-input"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "email" })}
                    name="email"
                    width="100%"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    data-hj-suppress
                    data-test-id="phone-number-input"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "phoneNumber" })}
                    name="phone"
                    width="100%"
                  />
                </Grid>
                <Grid xs={12} item>
                  <SelectInput
                    asyncSearch={(searchedQuery) =>
                      setSearchedPhrase(searchedQuery)
                    }
                    data-test-id="roles-input"
                    disabled={hasViewAdminPermission}
                    displaySearch
                    initialValue={{ id: adminRoleID, label: adminRoleName }}
                    items={formattedRoleList}
                    label={
                      <RequiredFieldLabel text={message({ id: "role" })} />
                    }
                    name="roles"
                    placeholder={message({ id: "searchForRole" })}
                    width="100%"
                    anyElementsToFetch={anyRolesToFetch}
                    loadMoreElements={() =>
                      !isValidating && setRolesSize(rolesSize + 1)
                    }
                  />
                </Grid>
                <Grid xs={12} item>
                  <Toggle
                    data-test-id="notifications-enabled"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "notificationsEnabled" })}
                    name="notificationsEnabled"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    data-hj-suppress
                    data-test-id="password"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "plainPassword" })}
                    name="plainPassword"
                    type="password"
                    width="100%"
                  />
                </Grid>
                <Grid xs={6} item>
                  <TextInput
                    data-hj-suppress
                    data-test-id="password-repeat"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "repeatPassword" })}
                    name="plainPassword2"
                    type="password"
                    width="100%"
                  />
                </Grid>
              </Grid>
            </FormSection>
            <AccessToken tokensList={tokensList} editMode={!!props.editMode} />
            <FormSection
              isLoading={!isFormDataReady}
              title={message({ id: "adminStatus" })}
            >
              <Grid container spacing={12}>
                <Grid xs={12} item>
                  <Toggle
                    data-hj-suppress
                    data-test-id="is-active"
                    disabled={hasViewAdminPermission}
                    label={message({ id: "isActive" })}
                    name="isActive"
                  />
                </Grid>
              </Grid>
            </FormSection>
            <Box mt={12} />
            <FormButtons
              disableSubmit={
                isSubmitting || hasViewAdminPermission || !isFormDataReady
              }
              onCancelClick={() => {
                navigate(links.settings);
              }}
              onSubmitClick={() =>
                props.editMode &&
                props.initialValues?.apiKey &&
                values.apiKey !== props.initialValues.apiKey
                  ? setOpen(true)
                  : handleSubmit()
              }
              {...(!props.editMode && { submitMessage: "addAdmin" })}
            />
            {!isSubmitting && (
              <LeaveSiteGuard
                warnBeforeRefresh
                isDirty={dirty}
                initialValues={initialValues}
                currentValues={values}
              />
            )}
            <ConfirmationModal
              action={() => {
                setOpen(false);
                return handleSubmit();
              }}
              close={() => setOpen(false)}
              cancelText={message({ id: "cancel" })}
              header="changeAPIHeader"
              info="changeAPIInfo"
              open={open}
              submitText="yes"
            />
          </Form>
        );
      }}
    </Formik>
  );
}
